import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { triggerHaptic } from "./use-haptics";

const TAB_ORDER = ["/", "/blocks", "/email", "/codes", "/expenses"];
const SWIPE_THRESHOLD = 50;
const VELOCITY_THRESHOLD = 0.3;
const QUICK_SWIPE_MIN = 20;
const VERTICAL_TOLERANCE = 30;

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isTracking: boolean;
}

export function useTabSwipe() {
  const navigate = useNavigate();
  const location = useLocation();
  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false,
  });

  const currentTabIndex = TAB_ORDER.indexOf(location.pathname);
  const isTabPage = currentTabIndex !== -1;

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isTabPage) return;

      // Don't track swipes that start on swipeable cards (they have their own swipe handling)
      const target = e.target as HTMLElement;
      if (target.closest("[data-swipeable]")) {
        return;
      }

      const touch = e.touches[0];
      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isTracking: true,
      };
    },
    [isTabPage]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchState.current.isTracking) return;

      const touch = e.touches[0];
      const deltaY = Math.abs(touch.clientY - touchState.current.startY);

      // If vertical movement exceeds tolerance, stop tracking (user is scrolling)
      if (deltaY > VERTICAL_TOLERANCE) {
        touchState.current.isTracking = false;
      }
    },
    []
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchState.current.isTracking || !isTabPage) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchState.current.startX;
      const deltaTime = Date.now() - touchState.current.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      touchState.current.isTracking = false;

      const isQuickSwipe = velocity > VELOCITY_THRESHOLD && Math.abs(deltaX) > QUICK_SWIPE_MIN;
      const isLongSwipe = Math.abs(deltaX) >= SWIPE_THRESHOLD;

      if (!isQuickSwipe && !isLongSwipe) return;

      if (deltaX > 0 && currentTabIndex > 0) {
        // Swipe right → previous tab
        triggerHaptic("light");
        navigate(TAB_ORDER[currentTabIndex - 1]);
      } else if (deltaX < 0 && currentTabIndex < TAB_ORDER.length - 1) {
        // Swipe left → next tab
        triggerHaptic("light");
        navigate(TAB_ORDER[currentTabIndex + 1]);
      }
    },
    [isTabPage, currentTabIndex, navigate]
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTabPage,
    currentTabIndex,
  };
}
